// src/pages/Users/News/components/NewsCard.jsx

import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import createDOMPurify from "dompurify";
import {
  FaCalendarAlt,
  FaEdit,
  FaEye,
  FaShareAlt,
  FaTimes,
  FaTrash,
  FaUserEdit,
} from "react-icons/fa";

import { deleteNews } from "../../../../services/newsServices";
import NewsFullModal from "../NewsFullModal";

import {
  FALLBACK_NEWS_IMG,
  formatNewsDate,
  getAuthorName,
  getNewsCover,
  getNewsDate,
} from "../utils/newsUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et gestion sécurisée des styles enrichis
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "news-card-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-7 text-slate-600",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-primary",
  "hover:prose-a:underline",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-primary",
  "prose-blockquote:bg-orange-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-orange-500/10",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // On masque les médias intégrés uniquement dans l’aperçu
  "[&_img]:hidden",
  "[&_iframe]:hidden",
  "[&_video]:hidden",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

const sanitizeHtml = (html = "") => {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
};

function onImgError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK_NEWS_IMG;
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function NewsCard({
  news,
  canModify,
  onDeleted,
  onEdited,
  onEdit,
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [isExcerptTruncated, setIsExcerptTruncated] =
    useState(false);

  const excerptRef = useRef(null);

  const cover = getNewsCover(news);
  const author = getAuthorName(news);
  const date = formatNewsDate(getNewsDate(news));

  /*
   * Le véritable contenu HTML est conservé.
   * Il sera limité visuellement par la hauteur du conteneur.
   */
  const excerptHtml = useMemo(() => {
    const html = news?.content || "";
    const plainContent = stripHtml(html);

    if (!plainContent) {
      return sanitizeHtml(
        '<p style="font-style: italic;">Contenu non renseigné.</p>',
      );
    }

    return sanitizeHtml(html);
  }, [news?.content]);

  /*
   * Vérifie si le contenu dépasse la hauteur maximale.
   */
  useLayoutEffect(() => {
    const element = excerptRef.current;

    if (!element) {
      setIsExcerptTruncated(false);
      return undefined;
    }

    let animationFrameId;

    const checkOverflow = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const hasOverflow =
          element.scrollHeight > element.clientHeight + 2;

        setIsExcerptTruncated(hasOverflow);
      });
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;

    resizeObserver?.observe(element);

    window.addEventListener("resize", checkOverflow);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [excerptHtml, news?.id]);

  const share = async () => {
    try {
      const url =
        `${window.location.origin}` +
        `${window.location.pathname}` +
        `#news-${news.id}`;

      await navigator.clipboard.writeText(url);

      alert("Lien copié dans le presse-papiers.");
    } catch (error) {
      console.error("Copie du lien impossible :", error);
      alert("Impossible de copier le lien.");
    }
  };

  const remove = async () => {
    try {
      await deleteNews(news.id);

      onDeleted?.();
      onEdited?.();
    } catch (error) {
      console.error(
        "Suppression actualité échouée :",
        error,
      );

      alert("Erreur lors de la suppression.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <article
        id={`news-${news.id}`}
        className={cn(
          "group flex h-full flex-col overflow-hidden rounded-[2rem]",
          "border border-slate-200 bg-white shadow-sm",
          "transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/10",
          "dark:border-slate-800 dark:bg-slate-900",
        )}
      >
        {/* Image */}

        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative block w-full overflow-hidden bg-slate-100 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-primary/40 dark:bg-slate-950"
          aria-label={`Lire ${news?.title || "actualité"}`}
        >
          <div className="aspect-[16/10] w-full">
            <img
              src={cover}
              alt={news?.title || "Illustration"}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
              loading="lazy"
              onError={onImgError}
            />
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent" />

          <div className="absolute left-4 top-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-950 shadow-sm">
              <FaCalendarAlt className="text-orange-500" />
              {date}
            </span>
          </div>

          {!news?.is_active ? (
            <div className="absolute right-4 top-4">
              <span className="inline-flex rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold uppercase text-amber-950 shadow-sm">
                Inactif
              </span>
            </div>
          ) : null}

          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="line-clamp-2 text-2xl font-bold leading-tight text-white drop-shadow-sm">
              {news?.title || "Sans titre"}
            </h3>
          </div>
        </button>

        {/* Corps de la carte */}

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
            <FaUserEdit />
            {author}
          </div>

          {/* Aperçu HTML enrichi */}

          <div className="relative min-h-[120px]">
            <div
              ref={excerptRef}
              className={cn(
                RICH_TEXT_CLASSES,
                "max-h-[150px] overflow-hidden pr-1",
              )}
              style={{ tabSize: 4 }}
              dangerouslySetInnerHTML={{
                __html: excerptHtml,
              }}
            />

            {isExcerptTruncated ? (
              <div
                className={cn(
                  "pointer-events-none absolute inset-x-0 bottom-0 h-20",
                  "bg-gradient-to-t from-white via-white/95 to-transparent",
                  "dark:from-slate-900 dark:via-slate-900/95",
                )}
                aria-hidden="true"
              />
            ) : null}
          </div>

          {/* Actions */}

          <div className="mt-auto flex items-center justify-between gap-3 pt-5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-xs font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
              >
                <FaEye />
                Lire
              </button>

              <button
                type="button"
                onClick={share}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaShareAlt />
                Partager
              </button>
            </div>

            {canModify ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onEdit?.(news)}
                  className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                  title="Modifier"
                  aria-label="Modifier"
                >
                  <FaEdit />
                </button>

                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
                  title="Supprimer"
                  aria-label="Supprimer"
                >
                  <FaTrash />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </article>

      {/* Modale de lecture */}

      {open ? (
        <NewsFullModal
          news={news}
          onClose={() => setOpen(false)}
        />
      ) : null}

      {/* Confirmation de suppression */}

      {confirming &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm"
            onClick={(event) => {
              if (event.currentTarget === event.target) {
                setConfirming(false);
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Confirmer la suppression"
          >
            <div
              className="w-full max-w-md overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-rose-700 via-pink-700 to-orange-600 px-5 py-4 text-white">
                <h4 className="font-bold">
                  Supprimer cette actualité ?
                </h4>

                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 transition hover:bg-white/25"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="p-5">
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Cette action est définitive. L’actualité sera
                  supprimée du fil du club.
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                  >
                    Annuler
                  </button>

                  <button
                    type="button"
                    onClick={remove}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-rose-600 px-4 text-sm font-bold text-white transition active:scale-95"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}